import { useEffect } from 'react'
import { connect } from 'react-redux'
import { parse } from 'querystringify'
import { createSelector } from 'reselect'

// Actions
import { actions as payActions } from 'modules/pay/actions'
import { actions as userSongActions } from 'modules/usersongs/actions'

// Components
import { Tabs, CreateBox } from 'kit'
import { PageHeading, Section, EmptyState, Button } from '@tracklib/kit'
import IntroTooltip from 'components/usersongs/introtooltip'
import ReportRevenueToast from 'components/usersongs/reportrevenuetoast'
import SEO from 'components/base/seo'
import UserSongItem from 'components/usersongs/song/'

// Selectors
import { selectRequest } from 'modules/requests/selectors'
import { selectUserSongListWithSongs } from 'modules/usersongs/selectors'
import { selectAll, ownProfileEntity } from 'selectors'

// Types
import { ApiRequest, UserSong } from 'types'

import styles from './usersongs.css'

type UserSongsPageQuery = {
  filter: 'unlicensed' | 'incomplete' | 'active' | ''
}

type UserSongsProps = {
  // mapStateToProps
  activeQuery: UserSongsPageQuery
  fetchAllUserSongsRequest: ApiRequest
  userSongs: Array<UserSong>
  userHasPurchasedTracks: boolean
  userHasUserSongs: boolean
  // mapDispatchToProps
  fetchAllUserSongs: () => void
  fetchLatestRevenueOrder: () => void
}

const UserSongs = ({
  activeQuery,
  fetchAllUserSongsRequest,
  userSongs,
  userHasPurchasedTracks,
  userHasUserSongs,
  fetchAllUserSongs,
  fetchLatestRevenueOrder,
}: UserSongsProps): JSX.Element => {
  useEffect(() => {
    fetchAllUserSongs()
    fetchLatestRevenueOrder()
  }, [fetchAllUserSongs, fetchLatestRevenueOrder])

  const renderContent = () => {
    if (fetchAllUserSongsRequest.isDone && !userHasUserSongs) {
      return (
        <IntroTooltip
          title="Register your first song"
          subtitle="Welcome to the new home for all songs you’ve made using Tracklib samples – both licensed and unlicensed. Press this button to register a new song using a Tracklib sample."
          linkText="Learn more about your songs"
          linkUrl="/blog/tracklib-licensing-your-songs/"
        />
      )
    }
    return [
      ...(userSongs.length
        ? userSongs
            .sort((songA, songB) => new Date(songB.created_at).getTime() - new Date(songA.created_at).getTime())
            .map(userSong => <UserSongItem userSong={userSong} key={userSong.id} />)
        : []),
      ...(!fetchAllUserSongsRequest.isDone
        ? [1, 2, 3, 4, 5, 6].slice(0, -(+userSongs?.length + 1)).map(id => <li key={String(id)} className={styles.loadingBox} />)
        : []),
    ]
  }

  return (
    <>
      <SEO title="Your songs | Tracklib" />
      <ReportRevenueToast />
      <Section size="medium">
        <PageHeading
          heading="Your songs"
          description="Create, register and license your songs created with samples via Tracklib all in one place."
          noPad
          wrapperClassName={styles.pageHeadingWrapper}
        />
        <div className={styles.wrapper}>
          {fetchAllUserSongsRequest.isDone && !userHasPurchasedTracks && !userSongs.length ? (
            <EmptyState
              emoji="looking"
              text="You haven’t registered any new songs using Tracklib samples yet."
              subtitle="To create a new song you first need to download or purchase one or more tracks for sampling."
            >
              <Button to="/tracks/">Browse tracks</Button>
            </EmptyState>
          ) : (
            <>
              <div className={styles.newHeader}>
                <Tabs
                  items={[
                    {
                      id: 1,
                      label: 'All',
                      query: {},
                      to: '/yoursongs',
                    },
                    {
                      id: 2,
                      label: 'Unlicensed',
                      query: { filter: 'unlicensed' },
                      to: '/yoursongs',
                    },
                    {
                      id: 3,
                      label: 'Incomplete',
                      query: { filter: 'incomplete' },
                      to: '/yoursongs',
                    },
                    {
                      id: 4,
                      label: 'Active',
                      query: { filter: 'active' },
                      to: '/yoursongs',
                    },
                  ]}
                  activeQuery={activeQuery}
                />
              </div>

              <ul className={styles.grid}>
                <CreateBox
                  to="usersong"
                  modal
                  text="Register new song"
                  className={styles.createSong}
                  datatesting="button_register_new_song"
                />
                {renderContent()}
              </ul>
            </>
          )}
        </div>
      </Section>
    </>
  )
}

const mapStateToProps = createSelector([selectAll, ownProfileEntity], (state, profile) => {
  const { search } = state.router.location
  const query = parse(search) as UserSongsPageQuery
  const { userSongs } = state.entities
  return {
    userSongs: selectUserSongListWithSongs(
      state,
      Object.keys(userSongs)
        .map(id => userSongs[id])
        .filter(({ state: userSongState }) => {
          if (query.filter === 'unlicensed') {
            return userSongState === 'UNLICENSED'
          }
          if (query.filter === 'incomplete') {
            return userSongState === 'INCOMPLETE'
          }
          if (query.filter === 'active') {
            return userSongState === 'ACTIVE'
          }
          return true
        }),
    ),
    activeQuery: query,
    fetchAllUserSongsRequest: selectRequest(state, 'FETCH_ALL_USER_SONGS'),
    userHasPurchasedTracks: profile?.purchased_tracks?.length,
    userHasUserSongs: Object.keys(state.entities.userSongs).length > 0,
  }
})

const mapDispatchToProps = {
  fetchAllUserSongs: userSongActions.fetchAllUserSongs,
  fetchLatestRevenueOrder: payActions.fetchLatestRevenueOrder,
}

export { UserSongs as Component }
export default connect(mapStateToProps, mapDispatchToProps)(UserSongs)
